import React, { useState, useEffect } from 'react';
import { Icon } from '@ohif/ui';
import { updateAIData } from '@custom/Apis/apis';
import { servicesManager } from '../../../../../platform/app/src/App';
import { useSelector } from 'react-redux';
import { RootState } from '@custom/store/store';

// Define the structure of our hemorrhage data
interface HemorrhageData {
  intraparenchymal: boolean;
  intraventricular: boolean;
  subarachnoid: boolean;
  subdural: boolean;
  epidural: boolean;
  any: boolean;
}

interface EditableFieldProps {
  value: boolean;
  onChange: (value: boolean) => void;
  isEditing: boolean;
  className?: string;
}

const EditableField = ({ value, onChange, isEditing, className = '' }: EditableFieldProps) => {
  if (isEditing) {
    return (
      <select
        value={value.toString()}
        onChange={(e) => {
          onChange(e.target.value === 'true');
        }}
        className={`bg-gray-800 text-white border-2 border-blue-500 rounded-md px-3 py-1 w-20 text-center focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent hover:bg-gray-700 transition-all duration-200 cursor-pointer ${className}`}
      >
        <option value="false" className="bg-gray-800 text-white">No</option>
        <option value="true" className="bg-gray-800 text-white">Yes</option>
      </select>
    );
  }
  return <span className={`${className} inline-block w-16 text-right`}>{value ? 'Yes' : 'No'}</span>;
};

interface SimpleModalProps {
  onClose: () => void;
  sopInstanceUID: string;
  seriesInstanceUID: string | null;
  aiData: Record<string, any> | null;
}

const SimpleModal = ({ onClose, sopInstanceUID, seriesInstanceUID, aiData }: SimpleModalProps): JSX.Element => {
  const { uiNotificationService } = servicesManager.services;
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<Record<string, boolean>>({});
  const [currentData, setCurrentData] = useState<HemorrhageData | null>(null);
  const userDetail = useSelector((state: RootState) => state?.getUserDetails);


  // Get the hemorrhage data for the current series and SOP
  const seriesData = aiData?.[seriesInstanceUID] || {};
  const hemorrhageData: Record<string, HemorrhageData> = seriesData[sopInstanceUID]
    ? { [sopInstanceUID]: seriesData[sopInstanceUID] }
    : {};


  useEffect(() => {
    setDataLoaded(false);

    if (sopInstanceUID && hemorrhageData && hemorrhageData[sopInstanceUID]) {
      setCurrentData(hemorrhageData[sopInstanceUID]);
    } else {
      setCurrentData(null);
    }
    setDataLoaded(true);
  }, [sopInstanceUID, hemorrhageData]);

  // Initialize edited data when currentData changes
  useEffect(() => {
    if (currentData) {
      setEditedData({
        'Intraparenchymal': currentData.intraparenchymal || false,
        'Intraventricular': currentData.intraventricular || false,
        'Subarachnoid': currentData.subarachnoid || false,
        'Subdural': currentData.subdural || false,
        'Epidural': currentData.epidural || false,
        'Any': currentData.any || false
      });
    }
  }, [currentData]);

  const handleUpdate = async () => {
    try {
      const hospitalId = localStorage.getItem('hospitalId') || sessionStorage.getItem('hospitalId');
      const currentUrl = window.location.href;
      const url = new URL(currentUrl);
      const studyInstanceUID = url.searchParams.get("StudyInstanceUIDs");

      if (!hospitalId || !studyInstanceUID) {
        throw new Error('Missing required parameters');
      }

      // Send boolean values directly to API
      const cleanData = editedData;
      await updateAIData(hospitalId, studyInstanceUID, seriesInstanceUID, sopInstanceUID, cleanData);

      uiNotificationService.show({
        title: 'Success',
        message: 'AI data updated successfully!',
        type: 'success',
        position: "topRight"
      });

      setIsEditing(false);
    } catch (error) {
      console.error('Error updating AI data:', error);
      uiNotificationService.show({
        title: 'Error',
        message: 'Failed to update AI data. Please try again.',
        type: 'error',
        position: "topRight"
      });
    }
  };

  const handleValueChange = (key: string, value: boolean) => {
    setEditedData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Convert hemorrhage data to series results format
  const seriesResults = currentData ? {
    [sopInstanceUID || 'default']: {
      [sopInstanceUID || 'default']: {
        'Intraparenchymal': editedData['Intraparenchymal'] || false,
        'Intraventricular': editedData['Intraventricular'] || false,
        'Subarachnoid': editedData['Subarachnoid'] || false,
        'Subdural': editedData['Subdural'] || false,
        'Epidural': editedData['Epidural'] || false,
        'Any': editedData['Any'] || false
      }
    }
  } : null;

  // Action buttons
  if (!dataLoaded) {
    return (
      <div className="bg-gray-900 text-white rounded-lg p-4 shadow-xl" style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        border: '1px solid #4a90e2',
        zIndex: 9999,
        width: '400px',
      }}>
        <div className="flex justify-between items-center mb-4 pb-2 border-b border-blue-500">
          <h3 className="text-blue-400 font-semibold">Loading...</h3>
          <button onClick={onClose} className="text-white hover:bg-gray-700 p-1 rounded">
            ×
          </button>
        </div>
        <div className="p-4 text-center">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-700 rounded w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-700 rounded"></div>
                <div className="h-4 bg-gray-700 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderActionButtons = () => (
    <div className="flex justify-end items-center mt-4 pt-3 border-t border-gray-700 text-xs">
      <div className="flex gap-2">
        {isEditing ? (
          <>
            <button
              onClick={() => {
                setEditedData({
                  'Intraparenchymal': currentData?.intraparenchymal || false,
                  'Intraventricular': currentData?.intraventricular || false,
                  'Subarachnoid': currentData?.subarachnoid || false,
                  'Subdural': currentData?.subdural || false,
                  'Epidural': currentData?.epidural || false,
                  'Any': currentData?.any || false
                });
                setIsEditing(false);
              }}
              className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdate}
              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
            >
              Update
            </button>
          </>
        ) : (userDetail?.result.aiTraining &&
          <button
            onClick={() => setIsEditing(true)}
            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
          >
            Edit
          </button>
        )}
        <button
          onClick={onClose}
          className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm"
        >
          Close
        </button>
      </div>
    </div>
  );

  if (!currentData) {
    return (
      <div className="bg-gray-900 text-white rounded-lg p-4 shadow-xl" style={{
        position: 'fixed',
        top: '-200px',
        left: '-200px',
        transform: 'translate(-50%, -50%)',
        border: '1px solid #4a90e2',
        zIndex: 9999,
        width: '400px',
      }}>
        <div className="flex justify-between items-center mb-4 pb-2 border-b border-blue-500">
          <h3 className="text-blue-400 font-semibold">Hemorrhage Detection Results</h3>
          <button onClick={onClose} className="text-white hover:bg-gray-700 p-1 rounded">
            ×
          </button>
        </div>
        <div className="p-4 text-center text-red-400">
          {/* <Icon name="warning" className="w-12 h-12 mx-auto mb-4" /> */}
          <p className="text-lg font-medium mb-2">No Data Available</p>
          <p className="text-sm text-gray-400">No hemorrhage data found for the selected SOP Instance.</p>
          <div className="text-sm text-gray-500 mb-2">
            <div>SOP Instance UID: {sopInstanceUID}</div>
            {seriesInstanceUID && (
              <div className="mt-1">Series Instance UID: {seriesInstanceUID}</div>
            )}
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
          >
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-gray-900 text-white rounded-lg p-4 shadow-xl"
      style={{
        position: 'fixed',
        top: '-200px',
        left: '-200px',
        transform: 'translate(-50%, -50%)',
        border: '1px solid #4a90e2',
        borderRadius: '4px',
        padding: '8px',
        color: 'white',
        zIndex: 9999,
        width: '400px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
        fontFamily: 'monospace',
        fontSize: '12px',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-between items-center mb-4 pb-2 border-b border-blue-500">
        <h3 className="text-blue-400 font-semibold">Hemorrhage Detection Results</h3>
        <button
          onClick={onClose}
          className="text-white hover:bg-gray-700 p-1 rounded"
        >
          ×
        </button>
      </div>

      <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
        {seriesResults ? (
          Object.entries(seriesResults).map(([seriesUID, sopMap]: [string, any]) => (
            <div key={seriesUID} className="border border-gray-700 rounded p-3 bg-gray-800">
              <div className="text-xs font-medium text-blue-400 break-all mb-2">
                SeriesInstanceUID: {seriesInstanceUID}
              </div>
              {Object.entries(sopMap || {}).map(([sopUID, metrics]: [string, any]) => (
                <div key={sopUID} className="ml-2 border-t border-gray-700 pt-2 mt-2">
                  <div className="text-xs font-medium text-gray-400 break-all mb-1">
                    SOPInstanceUID: {sopInstanceUID}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-2 text-xs">
                    {metrics && typeof metrics === 'object' ? (
                      Object.entries(metrics).map(([k, v]) => {
                        const value = Boolean(v);
                        const isHighlighted = k === 'Any';
                        return (
                          <div key={k} className="flex justify-between items-center">
                            <span className={isHighlighted ? 'text-blue-400' : 'text-gray-400'}>
                              {k}:
                            </span>
                            <EditableField
                              value={value}
                              onChange={(newValue) => handleValueChange(k, newValue)}
                              isEditing={isEditing}
                              className={`text-right ${value ? 'text-green-400' : 'text-gray-300'} ${isHighlighted ? 'font-bold' : ''}`}
                            />
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-xs text-gray-500">No data available</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))
        ) : (
          <div className="text-center py-4 text-gray-400">No data available</div>
        )}
      </div>

      {renderActionButtons()}
    </div>
  );
};

export default SimpleModal;
